/**
 * NorwayRouteMap — animated SVG of cargo routes pulsing across Norway.
 *
 * Norway silhouette and city positions are generated from real lat/lon points
 * via Mercator projection (rather than hand-drawn), so the country shape and
 * the city placements stay in geographic agreement. Routes draw in along
 * bezier arcs with a moving dot, mono labels flash beside each route, then the
 * cycle loops. Respects prefers-reduced-motion (renders all routes statically).
 */

import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

type CityKey =
  | "oslo"
  | "bergen"
  | "stavanger"
  | "kristiansand"
  | "trondheim"
  | "alesund"
  | "bodo"
  | "tromso";

// ── Projection ────────────────────────────────────────────────────────────────

const VIEW_W = 600;
const VIEW_H = 580;
const MARGIN = 36;
const LON_MIN = 3.0;
const LON_MAX = 32.0;
const LAT_MIN = 57.5;
const LAT_MAX = 71.5;

const mercY = (lat: number) => Math.log(Math.tan(Math.PI / 4 + (lat * Math.PI) / 360));
const MY_TOP = mercY(LAT_MAX);
const MY_BOT = mercY(LAT_MIN);
const MY_SPAN = MY_TOP - MY_BOT;

function project(lon: number, lat: number): { x: number; y: number } {
  return {
    x: ((lon - LON_MIN) / (LON_MAX - LON_MIN)) * (VIEW_W - 2 * MARGIN) + MARGIN,
    y: ((MY_TOP - mercY(lat)) / MY_SPAN) * (VIEW_H - 2 * MARGIN) + MARGIN,
  };
}

// ── Country outline (real coastal/border lat-lon, clockwise from Lindesnes) ──

const NORWAY_OUTLINE_LATLON: [number, number][] = [
  // South coast — Lindesnes east toward Oslofjord west side
  [7.05, 57.98], // Lindesnes
  [7.46, 58.03], // Mandal
  [7.99, 58.15], // Kristiansand
  [8.40, 58.25], // Lillesand
  [8.77, 58.46], // Arendal
  [9.23, 58.72], // Risør
  [10.04, 59.05], // Larvik
  [10.23, 59.13], // Sandefjord
  // Oslofjord notch
  [10.41, 59.27], // Tønsberg
  [10.62, 59.43], // Drammen entry
  [10.75, 59.91], // Oslo (innermost head of fjord)
  [10.66, 59.43], // Moss (east side of fjord)
  [11.10, 59.28], // Sarpsborg
  [11.39, 59.13], // Halden (border)
  // East border (smoothed Sweden border) NE
  [12.0, 60.19], // Kongsvinger
  [12.27, 61.31], // Trysil
  [11.95, 62.57], // Røros area
  [12.05, 63.78], // Stiklestad
  [13.10, 64.46], // Grong
  [13.80, 65.83], // Mosjøen
  [14.40, 66.31], // Mo i Rana
  [17.43, 68.43], // Narvik border
  // Finnmark cap stretching east
  [21.40, 69.46], // Kvænangen
  [23.04, 69.01], // Kautokeino
  [25.51, 69.47], // Karasjok
  [28.20, 69.66], // Tana
  [30.05, 69.73], // Kirkenes
  [31.10, 70.37], // Vardø (NE corner)
  // Top edge
  [27.69, 71.13], // Nordkinn
  [25.78, 71.17], // Nordkapp
  // North coast going west
  [23.69, 70.66], // Hammerfest
  [20.30, 70.18], // Sørøya
  [18.96, 69.80], // Outer coast at Tromsø latitude
  [16.05, 69.10], // Andøya
  [13.60, 68.13], // Lofoten east
  [12.70, 67.83], // Lofoten mid
  [12.10, 67.51], // Røst
  [12.60, 65.95], // Sandnessjøen
  [12.50, 65.50], // Mid Helgeland
  [11.00, 64.20], // Trondheimsfjord entry north
  [10.30, 64.20], // Trondheimsfjord west headland
  [9.50, 63.80], // Trondheimsfjord west coast
  [8.40, 63.43], // Smøla / Hitra
  // West coast through Stadlandet down to Lindesnes
  [5.10, 62.20], // Stadlandet (western tip)
  [5.03, 61.60], // Florø
  [4.90, 60.83], // Bremanger
  [5.32, 60.39], // Bergen coast
  [5.27, 59.41], // Haugesund
  [5.73, 58.97], // Stavanger coast
  [6.00, 58.45], // Egersund
];

const NORWAY_POINTS = NORWAY_OUTLINE_LATLON.map(([lon, lat]) => project(lon, lat));

const NORWAY_PATH = (() => {
  if (NORWAY_POINTS.length === 0) return "";
  const head = NORWAY_POINTS[0]!;
  const segs = [`M ${head.x.toFixed(1)} ${head.y.toFixed(1)}`];
  for (let i = 1; i < NORWAY_POINTS.length; i += 1) {
    const p = NORWAY_POINTS[i]!;
    segs.push(`L ${p.x.toFixed(1)} ${p.y.toFixed(1)}`);
  }
  segs.push("Z");
  return segs.join(" ");
})();

// ── Cities ────────────────────────────────────────────────────────────────────

const CITY_LATLON: Record<
  CityKey,
  { name: string; lon: number; lat: number; labelAnchor: "start" | "end"; labelDx?: number; labelDy?: number }
> = {
  oslo: { name: "Oslo", lon: 10.75, lat: 59.91, labelAnchor: "start", labelDx: 11 },
  bergen: { name: "Bergen", lon: 5.32, lat: 60.39, labelAnchor: "end", labelDx: -11 },
  stavanger: { name: "Stavanger", lon: 5.73, lat: 58.97, labelAnchor: "end", labelDx: -11 },
  kristiansand: { name: "Kristiansand", lon: 7.99, lat: 58.15, labelAnchor: "start", labelDx: 11, labelDy: 14 },
  trondheim: { name: "Trondheim", lon: 10.4, lat: 63.43, labelAnchor: "start", labelDx: 11 },
  alesund: { name: "Ålesund", lon: 6.15, lat: 62.47, labelAnchor: "end", labelDx: -11 },
  bodo: { name: "Bodø", lon: 14.4, lat: 67.28, labelAnchor: "start", labelDx: 11 },
  tromso: { name: "Tromsø", lon: 18.96, lat: 69.65, labelAnchor: "start", labelDx: 11 },
};

const CITIES = Object.fromEntries(
  Object.entries(CITY_LATLON).map(([key, c]) => [
    key,
    {
      name: c.name,
      labelAnchor: c.labelAnchor,
      labelDx: c.labelDx ?? 11,
      labelDy: c.labelDy ?? 4.5,
      ...project(c.lon, c.lat),
    },
  ]),
) as Record<
  CityKey,
  { name: string; x: number; y: number; labelAnchor: "start" | "end"; labelDx: number; labelDy: number }
>;

// ── Routes ────────────────────────────────────────────────────────────────────

type CargoKey = "furniture" | "vehicles" | "boats" | "machinery" | "pallets" | "other";

type Route = { from: CityKey; to: CityKey; km: number; cargo: CargoKey };

const ROUTES: Route[] = [
  { from: "oslo", to: "bergen", km: 463, cargo: "furniture" },
  { from: "stavanger", to: "kristiansand", km: 230, cargo: "vehicles" },
  { from: "trondheim", to: "alesund", km: 290, cargo: "machinery" },
  { from: "bergen", to: "stavanger", km: 207, cargo: "pallets" },
  { from: "oslo", to: "trondheim", km: 491, cargo: "boats" },
  { from: "tromso", to: "bodo", km: 558, cargo: "other" },
];

const ROUTE_DURATION_S = 2.6;
const ROUTE_GAP_MS = 1700;

function controlPoint(from: CityKey, to: CityKey): { x: number; y: number } {
  const a = CITIES[from];
  const b = CITIES[to];
  const dx = b.x - a.x;
  const dy = b.y - a.y;
  const length = Math.hypot(dx, dy);
  const bow = Math.min(70, length * 0.22);
  const direction = a.x < b.x ? -1 : 1;
  return {
    x: (a.x + b.x) / 2 + (-dy / length) * bow * direction,
    y: (a.y + b.y) / 2 + (dx / length) * bow * direction,
  };
}

function buildRoutePath(from: CityKey, to: CityKey): string {
  const a = CITIES[from];
  const b = CITIES[to];
  const c = controlPoint(from, to);
  return `M ${a.x.toFixed(1)} ${a.y.toFixed(1)} Q ${c.x.toFixed(1)} ${c.y.toFixed(1)} ${b.x.toFixed(1)} ${b.y.toFixed(1)}`;
}

function sampleQuadratic(
  a: { x: number; y: number },
  c: { x: number; y: number },
  b: { x: number; y: number },
  samples = 7,
): { xs: number[]; ys: number[] } {
  const xs: number[] = [];
  const ys: number[] = [];
  for (let i = 0; i < samples; i += 1) {
    const t = i / (samples - 1);
    const u = 1 - t;
    xs.push(u * u * a.x + 2 * u * t * c.x + t * t * b.x);
    ys.push(u * u * a.y + 2 * u * t * c.y + t * t * b.y);
  }
  return { xs, ys };
}

// ── Component ─────────────────────────────────────────────────────────────────

export function NorwayRouteMap() {
  const { t } = useTranslation();
  const prefersReduced = useReducedMotion();
  const [activeIndex, setActiveIndex] = useState(0);

  useEffect(() => {
    if (prefersReduced) return;
    const id = window.setInterval(() => {
      setActiveIndex((i) => (i + 1) % ROUTES.length);
    }, ROUTE_GAP_MS);
    return () => window.clearInterval(id);
  }, [prefersReduced]);

  const visibleRoutes = prefersReduced
    ? ROUTES.map((r, i) => ({ route: r, key: `static-${i}` }))
    : [{ route: ROUTES[activeIndex]!, key: `live-${activeIndex}` }];

  return (
    <div className="relative isolate w-full">
      <div className="relative h-[clamp(380px,52dvh,520px)] w-full overflow-hidden rounded-[2rem] border border-slate-800/60 bg-[radial-gradient(120%_70%_at_50%_30%,rgba(37,99,235,0.1),transparent_70%)]">
        <svg
          viewBox={`0 0 ${VIEW_W} ${VIEW_H}`}
          preserveAspectRatio="xMidYMid meet"
          className="absolute inset-0 h-full w-full"
          role="img"
          aria-label={t("about.map.label")}
        >
          <defs>
            <filter id="route-glow" x="-20%" y="-20%" width="140%" height="140%">
              <feGaussianBlur stdDeviation="2.4" result="blur" />
              <feMerge>
                <feMergeNode in="blur" />
                <feMergeNode in="SourceGraphic" />
              </feMerge>
            </filter>
            <linearGradient id="route-gradient" x1="0%" y1="0%" x2="100%" y2="0%">
              <stop offset="0%" stopColor="#60a5fa" stopOpacity="0.2" />
              <stop offset="60%" stopColor="#60a5fa" stopOpacity="1" />
              <stop offset="100%" stopColor="#dbeafe" stopOpacity="1" />
            </linearGradient>
            <pattern id="map-grid" width="30" height="30" patternUnits="userSpaceOnUse">
              <path d="M 30 0 L 0 0 0 30" fill="none" stroke="rgba(96,165,250,0.06)" strokeWidth="1" />
            </pattern>
            <radialGradient id="land-fill" cx="50%" cy="40%" r="70%">
              <stop offset="0%" stopColor="#0f1729" stopOpacity="0.95" />
              <stop offset="100%" stopColor="#0a1120" stopOpacity="0.85" />
            </radialGradient>
          </defs>

          {/* Sea grid */}
          <rect width={VIEW_W} height={VIEW_H} fill="url(#map-grid)" />

          {/* Norway silhouette */}
          <path
            d={NORWAY_PATH}
            fill="url(#land-fill)"
            stroke="rgba(96,165,250,0.45)"
            strokeWidth="1.1"
            strokeLinejoin="round"
          />
          {/* Inner soft border to give the coast a subtle inland glow */}
          <path
            d={NORWAY_PATH}
            fill="none"
            stroke="rgba(96,165,250,0.16)"
            strokeWidth="3"
            strokeLinejoin="round"
            style={{ filter: "blur(2px)" }}
          />

          {/* Cities */}
          {(Object.entries(CITIES) as [CityKey, (typeof CITIES)[CityKey]][]).map(([key, city], idx) => (
            <g key={key}>
              <motion.circle
                cx={city.x}
                cy={city.y}
                r={9}
                fill="rgba(96,165,250,0.22)"
                animate={prefersReduced ? undefined : { r: [5, 14, 5], opacity: [0.45, 0, 0.45] }}
                transition={{ duration: 3, repeat: Infinity, delay: idx * 0.35, ease: "easeOut" }}
              />
              <circle cx={city.x} cy={city.y} r={3.6} fill="#93c5fd" />
              <text
                x={city.x + city.labelDx}
                y={city.y + city.labelDy}
                textAnchor={city.labelAnchor}
                className="fill-slate-300 font-mono"
                style={{ fontSize: "13px", letterSpacing: "0.16em", textTransform: "uppercase" }}
              >
                {city.name}
              </text>
            </g>
          ))}

          {/* Routes */}
          <AnimatePresence mode={prefersReduced ? "sync" : "popLayout"}>
            {visibleRoutes.map(({ route, key }) => {
              const path = buildRoutePath(route.from, route.to);
              const a = CITIES[route.from];
              const b = CITIES[route.to];
              const ctrl = controlPoint(route.from, route.to);
              const samples = sampleQuadratic(a, ctrl, b);
              const labelX = ctrl.x;
              const labelY = ctrl.y;
              const cargoLabel = (t(`about.map.cargo.${route.cargo}`) as string).toUpperCase();

              if (prefersReduced) {
                return (
                  <g key={key} opacity={0.45}>
                    <path
                      d={path}
                      fill="none"
                      stroke="url(#route-gradient)"
                      strokeWidth="1.6"
                      strokeLinecap="round"
                    />
                    <text
                      x={labelX}
                      y={labelY - 8}
                      textAnchor="middle"
                      className="fill-slate-300 font-mono"
                      style={{ fontSize: "13px", letterSpacing: "0.16em" }}
                    >
                      {a.name.toUpperCase()} → {b.name.toUpperCase()} · {route.km} KM · {cargoLabel}
                    </text>
                  </g>
                );
              }

              return (
                <motion.g key={key} initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
                  <motion.path
                    d={path}
                    fill="none"
                    stroke="url(#route-gradient)"
                    strokeWidth="1.8"
                    strokeLinecap="round"
                    filter="url(#route-glow)"
                    initial={{ pathLength: 0, opacity: 0 }}
                    animate={{ pathLength: 1, opacity: [0, 1, 1, 0] }}
                    exit={{ opacity: 0 }}
                    transition={{
                      pathLength: { duration: ROUTE_DURATION_S * 0.7, ease: [0.22, 1, 0.36, 1] },
                      opacity: { duration: ROUTE_DURATION_S, times: [0, 0.15, 0.78, 1] },
                    }}
                  />
                  <motion.circle
                    r={3.6}
                    fill="#dbeafe"
                    filter="url(#route-glow)"
                    initial={{ cx: a.x, cy: a.y, opacity: 0 }}
                    animate={{
                      cx: samples.xs,
                      cy: samples.ys,
                      opacity: [0, 1, 1, 1, 1, 1, 0],
                    }}
                    transition={{
                      duration: ROUTE_DURATION_S * 0.7,
                      ease: [0.22, 1, 0.36, 1],
                    }}
                  />
                  <motion.circle
                    cx={b.x}
                    cy={b.y}
                    r={5}
                    fill="none"
                    stroke="#93c5fd"
                    strokeWidth="1.2"
                    initial={{ scale: 0.6, opacity: 0 }}
                    animate={{ scale: [0.6, 1.7, 0.6], opacity: [0, 0.9, 0] }}
                    transition={{
                      duration: ROUTE_DURATION_S * 0.55,
                      delay: ROUTE_DURATION_S * 0.55,
                      ease: "easeOut",
                    }}
                    style={{ transformOrigin: `${b.x}px ${b.y}px` }}
                  />
                  <motion.text
                    x={labelX}
                    y={labelY - 10}
                    textAnchor="middle"
                    className="fill-slate-100 font-mono"
                    style={{ fontSize: "13px", letterSpacing: "0.16em" }}
                    initial={{ opacity: 0, y: labelY - 4 }}
                    animate={{
                      opacity: [0, 1, 1, 0],
                      y: [labelY - 4, labelY - 10, labelY - 10, labelY - 14],
                    }}
                    transition={{ duration: ROUTE_DURATION_S, times: [0, 0.25, 0.78, 1], ease: "easeOut" }}
                  >
                    {a.name.toUpperCase()} → {b.name.toUpperCase()} · {route.km} KM · {cargoLabel}
                  </motion.text>
                </motion.g>
              );
            })}
          </AnimatePresence>
        </svg>
      </div>

      <div className="mt-4 flex items-center justify-between gap-4 text-slate-500">
        <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-brand-300/80">
          {t("about.map.label")}
        </span>
        <div className="hidden h-px flex-1 bg-slate-800 sm:block" />
        <span className="font-mono text-[10px] uppercase tracking-[0.22em]">
          {t("about.map.caption")}
        </span>
      </div>
    </div>
  );
}
